import { useState, useEffect } from "react";
import { AlertCircle, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCredits } from "@/hooks/useCreditBalance";
import { useAuth } from "@/hooks/useAuth";

/**
 * Composant LowCreditBanner - Bandeau d'avertissement pour solde de crédits à zéro
 * 
 * Ce composant affiche un bandeau d'avertissement en haut de l'application
 * lorsque le solde de crédits atteint zéro. Il permet à l'utilisateur de :
 * - Voir clairement qu'il n'a plus de crédits
 * - Accéder rapidement à la page d'achat de crédits
 * - Fermer temporairement le bandeau (il réapparaîtra au prochain rechargement)
 * 
 * Le bandeau utilise les mises à jour en temps réel via Supabase Realtime
 * pour s'afficher/masquer automatiquement selon le solde.
 * 
 * Requirements: 6.3
 */
interface LowCreditBannerProps {
  onBuyCredits: () => void;
  className?: string;
}

export const LowCreditBanner = ({ onBuyCredits, className = "" }: LowCreditBannerProps) => {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id || undefined;
  
  // Hook avec mises à jour en temps réel
  const { availableCredits, loading } = useCredits(organizationId);
  
  // État local pour gérer la fermeture temporaire du bandeau
  const [isDismissed, setIsDismissed] = useState(false);

  // Réinitialiser l'état de fermeture si les crédits changent
  useEffect(() => {
    if (availableCredits > 0) {
      setIsDismissed(false);
    }
  }, [availableCredits]);

  // Ne pas afficher le bandeau si :
  // - En cours de chargement
  // - L'utilisateur l'a fermé temporairement
  // - Le solde n'est pas à zéro
  if (loading || isDismissed || availableCredits > 0) {
    return null;
  }

  // Gestionnaire de fermeture temporaire
  const handleDismiss = () => {
    setIsDismissed(true);
  };

  return (
    <div 
      className={`w-full bg-amber-500/10 border-b border-amber-500/20 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Icône et message */}
          <div className="flex items-center gap-3 flex-1">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                Vous n'avez plus de crédits disponibles
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
                Rechargez votre compte pour continuer à lancer des campagnes
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Bouton CTA d'achat */}
            <Button
              size="sm"
              className="px-4 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#40E0D0' }}
              onClick={onBuyCredits}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Acheter des crédits
            </Button>

            {/* Bouton de fermeture */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-amber-500/20 text-amber-900 dark:text-amber-100"
              onClick={handleDismiss}
              aria-label="Fermer le bandeau"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
